/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Category Carousel 1 PD component — content-only category tiles (no category selector)
import { useRouteLoaderData } from 'react-router';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { DynamicImage } from '@/components/dynamic-image';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import type { Image } from '@/types';

/**
 * One tile in the carousel. Up to 10 tiles are exposed as independent PD attributes
 * so each image, label and link is independently authorable.
 */
/* v8 ignore start */
@Component('categoryCarouselOne', {
    name: 'Category Carousel 1',
    description:
        'Horizontally scrollable carousel of content-authored category tiles. No category selector — each tile image, label, and link is configured independently.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class CategoryCarouselOneMetadata {
    @AttributeDefinition({ id: 'heading', name: 'Section Heading', type: 'string' })
    heading?: string;

    // Tile 1
    @AttributeDefinition({ id: 'tile1Image', name: 'Tile 1 Image', type: 'image' })
    tile1Image?: Image;
    @AttributeDefinition({ id: 'tile1Label', name: 'Tile 1 Label', type: 'string' })
    tile1Label?: string;
    @AttributeDefinition({ id: 'tile1Link', name: 'Tile 1 Link', type: 'url' })
    tile1Link?: string;

    // Tile 2
    @AttributeDefinition({ id: 'tile2Image', name: 'Tile 2 Image', type: 'image' })
    tile2Image?: Image;
    @AttributeDefinition({ id: 'tile2Label', name: 'Tile 2 Label', type: 'string' })
    tile2Label?: string;
    @AttributeDefinition({ id: 'tile2Link', name: 'Tile 2 Link', type: 'url' })
    tile2Link?: string;

    // Tile 3
    @AttributeDefinition({ id: 'tile3Image', name: 'Tile 3 Image', type: 'image' })
    tile3Image?: Image;
    @AttributeDefinition({ id: 'tile3Label', name: 'Tile 3 Label', type: 'string' })
    tile3Label?: string;
    @AttributeDefinition({ id: 'tile3Link', name: 'Tile 3 Link', type: 'url' })
    tile3Link?: string;

    // Tile 4
    @AttributeDefinition({ id: 'tile4Image', name: 'Tile 4 Image', type: 'image' })
    tile4Image?: Image;
    @AttributeDefinition({ id: 'tile4Label', name: 'Tile 4 Label', type: 'string' })
    tile4Label?: string;
    @AttributeDefinition({ id: 'tile4Link', name: 'Tile 4 Link', type: 'url' })
    tile4Link?: string;

    // Tile 5
    @AttributeDefinition({ id: 'tile5Image', name: 'Tile 5 Image', type: 'image' })
    tile5Image?: Image;
    @AttributeDefinition({ id: 'tile5Label', name: 'Tile 5 Label', type: 'string' })
    tile5Label?: string;
    @AttributeDefinition({ id: 'tile5Link', name: 'Tile 5 Link', type: 'url' })
    tile5Link?: string;

    // Tile 6
    @AttributeDefinition({ id: 'tile6Image', name: 'Tile 6 Image', type: 'image' })
    tile6Image?: Image;
    @AttributeDefinition({ id: 'tile6Label', name: 'Tile 6 Label', type: 'string' })
    tile6Label?: string;
    @AttributeDefinition({ id: 'tile6Link', name: 'Tile 6 Link', type: 'url' })
    tile6Link?: string;

    // Tile 7
    @AttributeDefinition({ id: 'tile7Image', name: 'Tile 7 Image', type: 'image' })
    tile7Image?: Image;
    @AttributeDefinition({ id: 'tile7Label', name: 'Tile 7 Label', type: 'string' })
    tile7Label?: string;
    @AttributeDefinition({ id: 'tile7Link', name: 'Tile 7 Link', type: 'url' })
    tile7Link?: string;

    // Tile 8
    @AttributeDefinition({ id: 'tile8Image', name: 'Tile 8 Image', type: 'image' })
    tile8Image?: Image;
    @AttributeDefinition({ id: 'tile8Label', name: 'Tile 8 Label', type: 'string' })
    tile8Label?: string;
    @AttributeDefinition({ id: 'tile8Link', name: 'Tile 8 Link', type: 'url' })
    tile8Link?: string;
}
/* v8 ignore stop */

interface TileData {
    image?: Image;
    label?: string;
    link?: string;
}

function buildTiles(props: Record<string, unknown>): TileData[] {
    const tiles: TileData[] = [];
    for (let i = 1; i <= 8; i++) {
        const tile: TileData = {
            image: props[`tile${i}Image`] as Image | undefined,
            label: props[`tile${i}Label`] as string | undefined,
            link: props[`tile${i}Link`] as string | undefined,
        };
        if (tile.image?.url || tile.label) tiles.push(tile);
    }
    return tiles;
}

export default function CategoryCarouselOne(props: {
    heading?: string;
    tile1Image?: Image;
    tile1Label?: string;
    tile1Link?: string;
    tile2Image?: Image;
    tile2Label?: string;
    tile2Link?: string;
    tile3Image?: Image;
    tile3Label?: string;
    tile3Link?: string;
    tile4Image?: Image;
    tile4Label?: string;
    tile4Link?: string;
    tile5Image?: Image;
    tile5Label?: string;
    tile5Link?: string;
    tile6Image?: Image;
    tile6Label?: string;
    tile6Link?: string;
    tile7Image?: Image;
    tile7Label?: string;
    tile7Link?: string;
    tile8Image?: Image;
    tile8Label?: string;
    tile8Link?: string;
}) {
    const tiles = buildTiles(props as Record<string, unknown>);
    const { ref: sectionRef, inView } = useInView<HTMLElement>({ margin: '-60px' });

    // RGBARRY-CUSTOMIZATION: when this carousel is placed on the Product Listing Page type (the
    // category route), render its tiles 35% smaller than on the homepage. Detected purely client-side
    // via the PLP route's loader data — truthy only inside `routes/_app.category.$categoryId`, undefined
    // on the homepage — so no Page Designer metadata or server change is needed.
    const isCompact = Boolean(useRouteLoaderData('routes/_app.category.$categoryId'));
    // Request DIS-scaled sources sized to the actual render: homepage widths × 0.65 on the PLP, so the
    // DIS server delivers right-sized images for the smaller tiles rather than downscaling in the browser.
    const tileWidths = isCompact ? { base: 130, lg: 156 } : { base: 200, lg: 240 };

    function TileContent({ tile, i }: { tile: TileData; i: number }) {
        const inner = (
            <div
                className={cn('flex flex-col gap-3 df-reveal', inView && 'df-reveal--show')}
                style={{ transitionDelay: inView ? `${i * 50}ms` : '0ms' }}>
                <div className="relative overflow-hidden rounded-ui aspect-[3/4] bg-muted">
                    {tile.image?.url && (
                        <>
                            {/* RGBARRY-CUSTOMIZATION: animate the wrapper so DynamicImage's image fade cannot override the zoom transition. */}
                            <DynamicImage
                                src={tile.image.url}
                                alt={tile.label ?? ''}
                                widths={tileWidths}
                                className="absolute inset-0 w-full h-full transition-transform duration-700 ease-in-out motion-reduce:transition-none group-hover:scale-105 motion-reduce:group-hover:scale-100 [&_picture]:block [&_picture]:w-full [&_picture]:h-full"
                                imageProps={{
                                    className: 'w-full h-full object-cover',
                                }}
                            />
                            {/* END RGBARRY-CUSTOMIZATION */}
                        </>
                    )}
                </div>
                {tile.label && (
                    <p className="text-sm font-medium text-center underline underline-offset-2 text-foreground">
                        {tile.label}
                    </p>
                )}
            </div>
        );
        if (tile.link) {
            return (
                // RGBARRY-CUSTOMIZATION: preserve the authored image colors on hover; the wrapper handles zoom independently.
                <Link to={tile.link} className="group block">
                    {inner}
                </Link>
            );
        }
        return inner;
    }

    return (
        <section ref={sectionRef} className="bg-background py-10 lg:py-14">
            <div className="section-container px-6 lg:px-8">
                {props.heading && (
                    <p
                        className={cn(
                            'text-2xl font-semibold leading-8 tracking-[-0.02em] text-foreground mb-8 df-reveal',
                            inView && 'df-reveal--show'
                        )}>
                        {props.heading}
                    </p>
                )}
            </div>

            {/* Desktop: 6-column equal grid */}
            <div className="hidden lg:block section-container px-6 lg:px-8">
                <div className="grid grid-cols-6 gap-6">
                    {/* oxlint-disable react/no-array-index-key */}
                    {tiles.map((tile, i) => (
                        // RGBARRY-CUSTOMIZATION: on the PLP, constrain each tile to 65% of its column
                        // (centered) so the tile renders 35% smaller than on the homepage.
                        <div key={i} className={cn(isCompact && 'mx-auto w-[65%]')}>
                            <TileContent tile={tile} i={i} />
                        </div>
                    ))}
                    {/* oxlint-enable react/no-array-index-key */}
                </div>
            </div>

            {/* Mobile/tablet: swipable carousel showing ~2.5 tiles */}
            <div className="lg:hidden">
                <Carousel opts={{ align: 'start', dragFree: true }} className="w-full">
                    <CarouselContent className="pl-6">
                        {/* oxlint-disable react/no-array-index-key */}
                        {tiles.map((tile, i) => (
                            // RGBARRY-CUSTOMIZATION: 35% smaller tiles on the PLP → basis 38% × 0.65 ≈ 24.7%.
                            <CarouselItem
                                key={i}
                                className={cn('min-w-0 pr-4', isCompact ? 'basis-[24.7%]' : 'basis-[38%]')}>
                                <TileContent tile={tile} i={i} />
                            </CarouselItem>
                        ))}
                        {/* oxlint-enable react/no-array-index-key */}
                    </CarouselContent>
                </Carousel>
            </div>
        </section>
    );
}
